/**
 * Кросс-канальный стрим (W4): SSE-хаб + polling-фолбэк (спека §4.3).
 *
 * SSE доступен не всегда (EventSource не умеет Bearer — токен в query;
 * прокси могут буферизовать) → при ошибке/разрыве клиент переходит на
 * polling `GET /chat/messages?after=` каждые POLL_INTERVAL_S сек. Оба режима
 * шлют одни и те же события в UI; статус канала виден пользователю.
 *
 * Операции confirm-gate (D2, §8): SSE-событие `operation_proposed`; в
 * polling-режиме тот же сигнал даёт опрос `GET /operations/active`
 * (I-7: события каналов паритетны).
 */

import {
  type ChatMessage,
  type OperationCard,
  accessTokenForStream,
  getActiveOperation,
  getMessages,
} from "./api";
import { POLL_INTERVAL_S } from "./config";

export type StreamStatus = "live" | "poll" | "off";

export interface StreamHandlers {
  after?: number;
  onMessage(msg: ChatMessage): void;
  onStatus(status: StreamStatus): void;
  /** Новый активный черновик confirm-gate (дедуп — по operation_id). */
  onOperation?(card: OperationCard): void;
}

export function startStream(handlers: StreamHandlers): () => void {
  let stopped = false;
  let source: EventSource | null = null;
  let pollTimer: number | null = null;
  let lastId = handlers.after ?? 0;
  let lastOpId: string | null = null;

  const emitRow = (row: ChatMessage) => {
    if (row.id > lastId) lastId = row.id;
    handlers.onMessage(row);
  };

  const emitOperation = (card: OperationCard) => {
    if (card.operation_id === lastOpId) return;
    lastOpId = card.operation_id;
    handlers.onOperation?.(card);
  };

  // ── polling-фолбэк ─────────────────────────────────────────────────────────
  const poll = async () => {
    if (stopped) return;
    try {
      // API использует id > after: +1 пропускал первую строку после курсора.
      const { messages } = await getMessages(lastId);
      if (stopped) return;
      for (const row of messages) emitRow(row);
      handlers.onStatus("poll");
    } catch {
      if (stopped) return;
      handlers.onStatus("off");
    }
    try {
      const { operation } = await getActiveOperation();
      if (stopped) return;
      if (operation) emitOperation(operation);
    } catch {
      /* операция догрузится следующим тиком */
    }
    if (!stopped && pollTimer === null) {
      pollTimer = window.setTimeout(() => {
        pollTimer = null;
        void poll();
      }, POLL_INTERVAL_S * 1000);
    }
  };

  const startPolling = () => {
    if (stopped || pollTimer !== null) return;
    handlers.onStatus("poll");
    void poll();
  };

  // ── SSE ────────────────────────────────────────────────────────────────────
  const startSse = () => {
    const token = accessTokenForStream();
    if (!token || typeof EventSource === "undefined") {
      startPolling();
      return;
    }
    source = new EventSource(
      `/api/v1/chat/sse?token=${encodeURIComponent(token)}`,
    );
    source.addEventListener("open", () => {
      if (!stopped) handlers.onStatus("live");
    });
    source.addEventListener("message_complete", (ev) => {
      handlers.onStatus("live");
      emitRow(JSON.parse((ev as MessageEvent).data) as ChatMessage);
    });
    source.addEventListener("mirror_message", (ev) => {
      handlers.onStatus("live");
      emitRow(JSON.parse((ev as MessageEvent).data) as ChatMessage);
    });
    source.addEventListener("operation_proposed", (ev) => {
      handlers.onStatus("live");
      emitOperation(JSON.parse((ev as MessageEvent).data) as OperationCard);
    });
    source.addEventListener("error", () => {
      // Невосстановимая ошибка EventSource (или прокси режет) → polling.
      source?.close();
      source = null;
      startPolling();
    });
  };

  startSse();

  return () => {
    stopped = true;
    source?.close();
    if (pollTimer !== null) window.clearTimeout(pollTimer);
    handlers.onStatus("off");
  };
}
